/**
 * Size Guide runtime — one data-driven construct.
 *
 * Reads the SSR pool and RESOLVES which presentation to render (no variant
 * enum): a shoppable measurement table (with an optional how-to-measure card /
 * link-row and colour swatches), an uploaded-image gallery, or nothing. The
 * decision lives entirely in resolveSizeGuide() — a pure function of the data.
 *
 * Analytics (feature slug `size_chart`): open, close, select_size,
 * select_color, unit_toggle, add_to_cart, added_to_cart, add_to_cart_error.
 */
;(() => {
  if (window.__sai_fsgfrd01_initialized__) return
  window.__sai_fsgfrd01_initialized__ = true

  const SNIPPET_ID = 'fsgfrd01'
  const TAG = 'sai-fsgfrd01'
  const FEATURE_SLUG = 'size_chart'

  /**
   * Escape values injected into innerHTML attribute/text context. Size labels
   * routinely contain a literal `"` (e.g. `S (26-30")`); without escaping the
   * `data-size-label` attribute terminates early and the label round-trip
   * truncates, dropping the row out of variant lookup. `| json` handles JSON
   * string escaping, not the HTML-attribute context this lands in.
   */
  function esc(s) {
    return String(s ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;')
  }

  // The table displays a clean size name; the full label is kept for variant
  // matching. Size option values often carry a redundant trailing measurement
  // (e.g. `Female (5.5" - 7.5")`) that the measurement columns already show —
  // live drops it, so we strip a trailing parenthetical for display only.
  // Plain sizes (`M`, `UK 6`, `72" x 36"`) have no trailing `(…)` and are kept.
  function displaySizeLabel(label) {
    const s = String(label ?? '')
    const stripped = s.replace(/\s*\([^()]*\)\s*$/, '').trim()
    return stripped || s
  }

  function closeSvg() {
    return '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>'
  }
  function arrowSvg() {
    return '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="13 6 19 12 13 18"/></svg>'
  }
  function chevron(dir) {
    const pts = dir === 'left' ? '15 18 9 12 15 6' : '9 18 15 12 9 6'
    return `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="${pts}"/></svg>`
  }

  function readSnippetPool(node) {
    for (let i = 0; i < node.children.length; i++) {
      const child = node.children[i]
      if (child.tagName === 'SCRIPT' && child.hasAttribute('data-spectrum-snippet-pool')) {
        try {
          return JSON.parse(child.textContent)
        } catch (e) {
          return {}
        }
      }
    }
    return {}
  }

  // ── chart helpers ──
  function getColumns(chart) {
    if (!chart || !chart.sizes || !chart.sizes[0]) return []
    return chart.sizes[0].measurements.map((m) => m.name)
  }
  function getUnitOptions(chart) {
    if (!chart || !chart.sizes || !chart.sizes[0]) return [chart ? chart.unit : 'inch']
    const units = [chart.unit || 'inch']
    const first = chart.sizes[0].measurements[0]
    if (first && first.translations) {
      for (const t of first.translations) {
        if (t.unit && units.indexOf(t.unit) === -1 && !t.standard) units.push(t.unit)
      }
    }
    return units
  }
  function getMeasurementValue(measurement, unit, baseUnit) {
    if (unit === baseUnit) return measurement.value
    if (!measurement.translations) return measurement.value
    for (const t of measurement.translations) {
      if (t.unit === unit && !t.standard) return t.value
    }
    return measurement.value
  }
  function getBasisOptionPosition(pool, chart) {
    if (!pool || !pool.product || !pool.product.options || !chart) return null
    const basisName = chart.basisAttribute || 'Size'
    const want = String(basisName).trim().toLowerCase()
    for (let i = 0; i < pool.product.options.length; i++) {
      const got = String(pool.product.options[i].name ?? '').trim().toLowerCase()
      if (got === want) return i + 1
    }
    return null
  }
  // Variant axes that are NOT the size (table) axis or the colour axis — e.g.
  // "Hand" (Right/Left). Rendered as pill selectors inside the drawer so the
  // shopper completes the variant and adds to cart, matching live. Single-value
  // axes are skipped (nothing to choose). Data-driven: any such option qualifies.
  function getExtraAxes(pool, chart) {
    // Per-product opt-out: spectrum.hide_variant_picker suppresses the extra-axis
    // (Variant) selector in the size guide, matching the ftyfrd01 buybox gate.
    // Size + colour axes are unaffected (they don't route through here).
    if (pool && pool.hide_variant_picker) return []
    if (!pool || !pool.product || !pool.product.options) return []
    const sizePos = getBasisOptionPosition(pool, chart)
    const colorPos = pool.color_option ? pool.color_option.position : null
    const out = []
    pool.product.options.forEach((opt, i) => {
      const pos = opt.position || i + 1
      if (pos === sizePos || pos === colorPos) return
      const values = (opt.values || []).filter((v) => v != null && v !== '')
      if (values.length < 2) return
      out.push({ name: opt.name, position: pos, key: `option${pos}`, values })
    })
    return out
  }
  function findVariantsForSelection(pool, chart, sizeLabel, colorValue, extraSel) {
    if (!pool || !pool.product || !pool.product.variants) return []
    const sizePos = getBasisOptionPosition(pool, chart)
    if (!sizePos) return []
    const sizeKey = `option${sizePos}`
    const colorPos = pool.color_option ? pool.color_option.position : null
    const colorKey = colorPos ? `option${colorPos}` : null
    const extras = extraSel && typeof extraSel === 'object' ? extraSel : null
    const matches = []
    for (const v of pool.product.variants) {
      if (v[sizeKey] !== sizeLabel) continue
      if (colorKey && colorValue && v[colorKey] !== colorValue) continue
      if (extras) {
        let ok = true
        for (const key in extras) {
          if (extras[key] != null && v[key] !== extras[key]) { ok = false; break }
        }
        if (!ok) continue
      }
      matches.push(v)
    }
    return matches
  }
  function isSizeAvailable(pool, chart, sizeLabel, colorValue, extraSel) {
    return findVariantsForSelection(pool, chart, sizeLabel, colorValue, extraSel).some((v) => v.available)
  }
  function pickVariantForAtc(pool, chart, sizeLabel, colorValue, extraSel) {
    const variants = findVariantsForSelection(pool, chart, sizeLabel, colorValue, extraSel)
    return variants.find((v) => v.available) || variants[0] || null
  }

  /**
   * THE RESOLVER. data + props → render descriptor. This is the entire
   * "which variant renders" decision; nothing else in the snippet branches on it.
   * Not pure: for the explicit measure_* fields it normalizes pool.guide /
   * pool.guide_media into the legacy shape the card/sheet/link-row renderers
   * consume, so callers must not treat `pool` as unmodified after the call.
   */
  function resolveSizeGuide(pool) {
    const props = pool.props || {}
    const chart = pool.size_chart
    // Explicit how-to-measure fields (field == presentation). When any is set
    // they drive the render directly and synthesise the guide/guide_media shape
    // the card/link-row/sheet renderers consume — no drawer-mode inference.
    // measure_image → top card; measure_steps / measure_video → button + sheet.
    // measure_link_text is the authored link-row label; empty falls back to a
    // generic default at the render site (never product-category copy).
    const mImg = pool.measure_image
    const mCap = pool.measure_caption
    const mSteps = (pool.measure_steps || []).filter(Boolean)
    const mVideo = pool.measure_video
    const hasNewMeasure = !!(mImg || mSteps.length || mVideo)
    if (hasNewMeasure) {
      pool.guide = {
        name: 'How to Measure',
        description: mCap || null,
        media: mImg ? { type: 'image', url: mImg.url, alt: mImg.alt || '' } : mSteps[0] || null,
        media_desktop: null,
        video: mVideo ? { url: mVideo, poster: null, label: pool.measure_link_text || null } : null,
      }
      pool.guide_media = mSteps
    }
    const images = pool.images || []
    const videoUrl = pool.guide && pool.guide.video && pool.guide.video.url
    // Drawer body defaults to data-presence resolution (table → gallery →
    // video). `drawer_body` is an optional force-override for the rare product
    // that carries more than one shape and wants to pin a specific one. Data
    // still ENABLES it: forcing a body the product has no data for renders
    // nothing (fail-closed), so the override can never fabricate content.
    const forced = props.drawer_body
    const hasTable = !!(chart && chart.sizes && chart.sizes.length)
    let body = null
    if (forced === 'table') body = hasTable ? 'table' : null
    else if (forced === 'gallery') body = images.length ? 'gallery' : null
    else if (forced === 'video') body = videoUrl ? 'video' : null
    else {
      if (hasTable) body = 'table'
      else if (images.length) body = 'gallery'
      else if (videoUrl) body = 'video'
    }
    if (!body) return null

    const hasColor = !!(pool.color_option && pool.color_option.values && pool.color_option.values.length)
    const hasGuide = !!pool.guide
    const guideStyle = (chart && chart.guideStyle) === 'linkrow' ? 'linkrow' : 'card'

    // The drawer stacks every section the product has data for: how-to-measure
    // guide (top) → size table → colour swatches → measure-video link-row.
    // They coexist — a product with both a guide and a colour axis shows both
    // (matches live). The link-row appears whenever a how-to VIDEO is authored,
    // independent of the image card.
    const hasVideo = !!(pool.guide && pool.guide.video && pool.guide.video.url)
    // Card vs button is chosen by WHICH field is set, not by drawer mode.
    // measure_image → card; measure_steps / measure_video → button. The legacy
    // branch keeps the pre-migration mode-based resolution for charts not yet
    // migrated to the explicit fields.
    const showGuideCard = hasNewMeasure
      ? !!mImg
      : body === 'table' && hasGuide && guideStyle === 'card' && !!(pool.guide && pool.guide.media)
    const showGuideLinkrow = hasNewMeasure
      ? mSteps.length > 0 || !!mVideo
      : hasGuide && ((body === 'table' && guideStyle === 'linkrow') || hasVideo || (body === 'gallery' && !!(pool.guide && pool.guide.media)))
    const showColor = body === 'table' && hasColor
    const showExtras = body === 'table' && getExtraAxes(pool, chart).length > 0
    return {
      body,
      showGuideCard,
      showGuideLinkrow,
      showColor,
      showExtras,
      split: showGuideCard,
      basisLabel: chart ? chart.basisLabel || chart.basisAttribute || 'Size' : null,
      mediaType: pool.guide && pool.guide.media ? pool.guide.media.type : null,
    }
  }

  const SizeGuide = class extends HTMLElement {
    connectedCallback() {
      if (this._initialized) return
      this._initialized = true
      this._pool = null
      this._resolved = null
      this._unit = null
      this._pickedSize = null
      this._pickedColor = null
      this._pickedExtra = {}
      // Last variant id we've reconciled with — the echo guard so our own
      // `spectrum:variant-change` dispatch in _pickSize doesn't bounce back
      // through the forward listener and re-render the chart.
      this._extVariantId = null
      this._galleryIndex = 0
      this._track = () => {}
      this._emit = () => {}
    }

    setAnalytics(track, emit) {
      this._track = typeof track === 'function' ? track : () => {}
      this._emit = typeof emit === 'function' ? emit : () => {}
    }

    applyVariant(_content, pool) {
      this._pool = pool && typeof pool === 'object' ? pool : {}
      this._resolved = resolveSizeGuide(this._pool)
      if (!this._resolved) return
      const chart = this._pool.size_chart
      if (chart && !this._unit) this._unit = chart.unit || 'inch'
      if (this._pool.color_option) this._pickedColor = this._pool.color_option.selected || null
      // Preselect the current variant's size so the drawer opens with a row
      // highlighted and the CTA enabled (the PDP's selection carries in).
      if (chart && !this._pickedSize) {
        const p = this._pool.product || {}
        const cur = (p.variants || []).find((v) => String(v.id) === String(p.current_variant_id))
        const pos = getBasisOptionPosition(this._pool, chart)
        const val = cur && pos ? cur['option' + pos] : null
        if (val && (chart.sizes || []).some((s) => s.label === val)) this._pickedSize = val
      }
      // Preselect each extra axis (e.g. Hand) from the current variant so the
      // drawer opens with a full, valid selection and the CTA resolves.
      const extraAxes = getExtraAxes(this._pool, chart)
      if (extraAxes.length) {
        const p = this._pool.product || {}
        const cur = (p.variants || []).find((v) => String(v.id) === String(p.current_variant_id))
        for (const ax of extraAxes) {
          if (this._pickedExtra[ax.key] != null) continue
          const curVal = cur ? cur[ax.key] : null
          this._pickedExtra[ax.key] = curVal && ax.values.includes(curVal) ? curVal : ax.values[0]
        }
      }
      this._render()
      this._bindEvents()
    }

    _props() {
      return (this._pool && this._pool.props) || {}
    }

    _render() {
      const props = this._props()
      const style = props.trigger_style || 'link'
      const text = props.trigger_text || 'Size Guide'
      let trigger
      if (style === 'icon') {
        trigger = `<button type="button" class="sai-fsgfrd01__trigger sai-fsgfrd01__trigger--icon" data-action="open" aria-label="${esc(text)}"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M4 21V3h16v18H4z"/><path d="M9 3v18M4 8h16M4 14h16"/></svg></button>`
      } else if (style === 'button') {
        trigger = `<button type="button" class="sai-fsgfrd01__trigger sai-fsgfrd01__trigger--button" data-action="open">${esc(text)}</button>`
      } else if (style === 'row') {
        // Full-width banner row (ruler + label left, arrow right) — the designed
        // trigger that sits under the size axis. Owned here (not proxied by the
        // size-selector snippet) so the drawer + its trigger are one construct.
        trigger =
          `<button type="button" class="sai-fsgfrd01__trigger sai-fsgfrd01__trigger--row" data-action="open">` +
          `<span class="sai-fsgfrd01__trigger-left">` +
          `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M3 8h18v8H3z"/><path d="M7 8v3M11 8v4M15 8v3M19 8v4"/></svg>` +
          `<span class="sai-fsgfrd01__trigger-text">${esc(text)}</span></span>` +
          `<svg class="sai-fsgfrd01__trigger-arrow" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="13 6 19 12 13 18"/></svg>` +
          `</button>`
      } else {
        trigger = `<button type="button" class="sai-fsgfrd01__trigger sai-fsgfrd01__trigger--link" data-action="open">${esc(text)}</button>`
      }
      // Drop any previously portaled overlay before re-rendering.
      if (this._rootEl && this._rootEl.parentElement === document.body) this._rootEl.remove()
      this.innerHTML = trigger + this._buildModal()
      this._rootEl = this.querySelector('.sai-fsgfrd01__root')
      // On re-render (events already wired), attach the handler to the fresh root.
      if (this._eventsBound && this._onClick && this._rootEl) this._rootEl.addEventListener('click', this._onClick)
    }

    _buildModal() {
      const r = this._resolved
      // The CTA is a panel child BELOW the scrollable body (not inside it):
      // it stays pinned while the content scrolls, like the live drawer.
      const g = this._pool.guide || {}
      const bodyInner =
        r.body === 'table'
          ? (r.showGuideCard ? this._mediaCard() : '') +
            `<div class="sai-fsgfrd01__main">${this._tableCard()}${r.showColor ? this._colorRow() : ''}${r.showExtras ? this._extraRows() : ''}${r.showGuideLinkrow ? this._guideLinkrow() : ''}</div>`
          : r.body === 'video'
            ? `<div class="sai-fsgfrd01__main"><div class="sai-fsgfrd01__media"><video controls playsinline${g.video && g.video.poster ? ` poster="${esc(g.video.poster)}"` : ''}><source src="${esc(g.video ? g.video.url : '')}" /></video></div></div>`
            : `<div class="sai-fsgfrd01__main">${this._gallery()}${r.showGuideLinkrow ? this._guideLinkrow() : ''}</div>`

      return (
        `<div class="sai-fsgfrd01__root" data-open="false" data-split="${r.split}" role="dialog" aria-modal="true">` +
        '<div class="sai-fsgfrd01__backdrop" data-action="close"></div>' +
        '<div class="sai-fsgfrd01__panel">' +
        `<div class="sai-fsgfrd01__head"><h2 class="sai-fsgfrd01__title">${esc(this._props().modal_title || 'Size Guide')}</h2>` +
        `<button type="button" class="sai-fsgfrd01__close" data-action="close" aria-label="Close">${closeSvg()}</button></div>` +
        `<div class="sai-fsgfrd01__body">${bodyInner}</div>` +
        this._foot() +
        '</div>' +
        (r.showGuideLinkrow ? this._guideSheet() : '') +
        '</div>'
      )
    }

    // Nested media sheet opened by the "Measure your perfect fit" link-row —
    // shows the guide image/video inline (a second drawer), not a new tab.
    _guideSheet() {
      const g = this._pool.guide || {}
      const list = this._pool.guide_media || []
      // Sheet content priority: the authored how-to VIDEO → dedicated
      // `guide_media` files → the guide's own media.
      const items = g.video && g.video.url
        ? [{ type: 'video', url: g.video.url, poster: g.video.poster || null, alt: g.name || 'How to measure' }]
        : list.length ? list : g.media ? [g.media] : []
      if (!items.length) return ''
      const isRow = items.length > 1
      const body = items
        .map((m) => {
          const alt = esc(m.alt || g.name || 'How to measure')
          return m.type === 'video'
            ? `<video loading="lazy" decoding="async" class="sai-fsgfrd01__guide-media" controls playsinline${m.poster ? ` poster="${esc(m.poster)}"` : ''} aria-label="${alt}"><source src="${esc(m.url)}" /></video>`
            : `<img loading="lazy" decoding="async" class="sai-fsgfrd01__guide-media" src="${esc(m.url)}" alt="${alt}" />`
        })
        .join('')
      return (
        '<div class="sai-fsgfrd01__guide" data-open="false">' +
        '<div class="sai-fsgfrd01__guide-backdrop" data-action="guide-close"></div>' +
        '<div class="sai-fsgfrd01__panel sai-fsgfrd01__guide-panel">' +
        '<div class="sai-fsgfrd01__head">' +
        `<h2 class="sai-fsgfrd01__title">${esc(g.name || 'How to Measure')}</h2>` +
        `<button type="button" class="sai-fsgfrd01__close" data-action="guide-close" aria-label="Close">${closeSvg()}</button>` +
        '</div>' +
        `<div class="sai-fsgfrd01__guide-body${isRow ? ' sai-fsgfrd01__guide-body--row' : ''}">${body}</div>` +
        '<div class="sai-fsgfrd01__foot"><button type="button" class="sai-fsgfrd01__buy sai-fsgfrd01__buy--back" data-action="close">Back to Shopping</button></div>' +
        '</div></div>'
      )
    }

    _mediaCard() {
      // Image-only, by design. The how-to-measure media is a self-contained
      // diagram/infographic — the snippet does not add a heading or description
      // (that text lives inside the uploaded image). `alt` carries the a11y text.
      // Compositions are viewport-specific: `media` is the mobile (landscape)
      // asset; `media_desktop`, when authored, swaps in inside the split column.
      const g = this._pool.guide
      if (!g || !g.media) return ''
      const alt = esc(g.media.alt || g.name || 'How to measure')
      if (g.media.type === 'video') {
        return `<div class="sai-fsgfrd01__media"><video controls playsinline${g.media.poster ? ` poster="${esc(g.media.poster)}"` : ''} aria-label="${alt}"><source src="${esc(g.media.url)}" /></video></div>`
      }
      const d = g.media_desktop && g.media_desktop.url ? g.media_desktop : null
      const dImg = d ? `<img class="sai-fsgfrd01__media-d" src="${esc(d.url)}" alt="${alt}" loading="lazy" decoding="async" />` : ''
      return `<div class="sai-fsgfrd01__media${d ? ' sai-fsgfrd01__media--dual' : ''}"><img loading="lazy" decoding="async" class="sai-fsgfrd01__media-m" src="${esc(g.media.url)}" alt="${alt}" />${dImg}</div>`
    }

    _tableCard() {
      const chart = this._pool.size_chart
      const baseUnit = chart.unit || 'inch'
      const measurements = getColumns(chart)
      let units = getUnitOptions(chart)
      // Every unit is its own column (no toggle). Metric before imperial to
      // match live — e.g. "Circumference (CM)" then "Circumference (Inches)".
      if (units.length > 1) units = units.slice().sort((a, b) => (a === 'inch' ? 1 : 0) - (b === 'inch' ? 1 : 0))
      const unitLbl = (u) => (u === 'inch' ? 'Inches' : String(u).toUpperCase())
      const cols = []
      measurements.forEach((mName, mi) => {
        // Two-line header per the design: measurement name (grey) over the
        // unit "(CM)" / "(Inches)" (dark, medium).
        if (units.length > 1) for (const u of units) cols.push({ mi, unit: u, label: `${esc(mName)}<b>(${esc(unitLbl(u))})</b>` })
        else cols.push({ mi, unit: baseUnit, label: esc(mName) })
      })

      let thead = `<div class="sai-fsgfrd01__thead"><div class="sai-fsgfrd01__th">${esc(this._resolved.basisLabel)}</div>`
      for (const c of cols) thead += `<div class="sai-fsgfrd01__th">${c.label}</div>`
      thead += '</div>'

      // oos_display prop (Studio): strike (default) | hide | disable. Table is JS-only (no SSR row).
      const oosMode = this._props().oos_display || 'strike'
      let rows = '<div class="sai-fsgfrd01__rows">'
      for (const size of chart.sizes) {
        const avail = isSizeAvailable(this._pool, chart, size.label, this._pickedColor, this._pickedExtra)
        if (!avail && oosMode === 'hide') continue
        const disabled = !avail && oosMode === 'disable'
        const sel = this._pickedSize === size.label
        const cls = ['sai-fsgfrd01__row', sel ? 'is-sel' : '', avail ? '' : 'is-oos', disabled ? 'is-disabled' : ''].filter(Boolean).join(' ')
        rows += `<div class="${cls}"${disabled ? ' aria-disabled="true"' : ' data-action="pick-size"'} data-size-label="${esc(size.label)}"><div class="sai-fsgfrd01__cell">${esc(displaySizeLabel(size.label))}</div>`
        for (const c of cols) rows += `<div class="sai-fsgfrd01__cell">${esc(getMeasurementValue(size.measurements[c.mi], c.unit, baseUnit))}</div>`
        rows += '</div>'
      }
      rows += '</div>'
      return `<div class="sai-fsgfrd01__card">${thead}${rows}</div>`
    }

    _guideLinkrow() {
      const g = this._pool.guide
      const isMp4 = (u) => /\.mp4(\?|$)/i.test(String(u || ''))
      const videoUrl = (g.video && g.video.url) || (g.media && isMp4(g.media.url) ? g.media.url : null) || null
      const mediaImg = g.media && g.media.url && !isMp4(g.media.url) ? g.media.url : null
      const poster = (g.video && g.video.poster) || null
      let thumb = ''
      if (videoUrl) {
        // Play the video in the thumbnail. Its auto-generated poster is often a
        // blank first frame (time 0), so the moving preview is what the shopper
        // should see. Safe to autoplay: the drawer root is display:none until
        // opened, so nothing loads while the drawer is closed.
        thumb = `<span class="sai-fsgfrd01__linkrow-thumb"><video src="${esc(videoUrl)}"${poster ? ` poster="${esc(poster)}"` : ''} muted autoplay loop playsinline preload="none"></video></span>`
      } else if (mediaImg || poster) {
        // No video — a real how-to-measure image (or an image poster).
        thumb = `<span class="sai-fsgfrd01__linkrow-thumb"><img src="${esc(mediaImg || poster)}" alt=""></span>`
      }
      // Label precedence: authored per-video label → chart's measure_link_text →
      // generic default. The default must stay category-neutral (this snippet
      // renders on mattresses and braces, not just footwear).
      const label = (g.video && g.video.label) || this._pool.measure_link_text || 'How to measure'
      return `<div class="sai-fsgfrd01__linkrow" data-action="guide-open">${thumb}<span class="sai-fsgfrd01__linkrow-label">${esc(label)}</span><span class="sai-fsgfrd01__linkrow-arrow">${arrowSvg()}</span></div>`
    }

    _colorRow() {
      const c = this._pool.color_option
      let sw = ''
      for (const v of c.values) {
        const sel = v.name === this._pickedColor
        // Frido swatch tile: a dedicated image full-bleeds the tile; a solid
        // colour renders as the inset 40×40 chip; neither present falls back to
        // a neutral chip.
        const inner = v.swatchImage
          ? `<img src="${esc(v.swatchImage)}" alt="${esc(v.name)}" loading="lazy" decoding="async">`
          : `<span class="chip"><span style="background:${esc(v.swatchColor || '#d9d9d9')}"></span></span>`
        sw += `<button type="button" class="sai-fsgfrd01__swatch${sel ? ' is-sel' : ''}" data-action="color" data-color-value="${esc(v.name)}" aria-pressed="${sel ? 'true' : 'false'}" aria-label="${esc(c.name)}: ${esc(v.name)}">${inner}</button>`
      }
      return `<div class="sai-fsgfrd01__color"><div class="sai-fsgfrd01__color-label">${esc(c.name)}: <b data-color-label>${esc(this._pickedColor || '')}</b></div><div class="sai-fsgfrd01__swatches">${sw}</div></div>`
    }

    // Secondary variant axes (e.g. Hand: Right/Left) as pill selectors — one card
    // per axis, mirroring the colour card. Selection folds into variant matching.
    _extraRows() {
      const axes = getExtraAxes(this._pool, this._pool.size_chart)
      let html = ''
      for (const ax of axes) {
        const picked = this._pickedExtra[ax.key] || ''
        let pills = ''
        for (const val of ax.values) {
          const sel = val === picked
          pills += `<button type="button" class="sai-fsgfrd01__opt${sel ? ' is-sel' : ''}" data-action="extra" data-extra-key="${esc(ax.key)}" data-extra-value="${esc(val)}" aria-pressed="${sel ? 'true' : 'false'}">${esc(val)}</button>`
        }
        html += `<div class="sai-fsgfrd01__optaxis"><div class="sai-fsgfrd01__opt-label">${esc(ax.name)}: <b data-extra-label="${esc(ax.key)}">${esc(picked)}</b></div><div class="sai-fsgfrd01__opts">${pills}</div></div>`
      }
      return html
    }

    _gallery() {
      const imgs = this._pool.images || []
      const props = this._props()
      // 'single'/'carousel' are deliberate merchant choices and pin the layout.
      // Everything else (including the legacy 'stacked' default) auto-resolves:
      // multiple uploaded chart images become a horizontal scroll row (each card
      // peeks the next), a lone image stays a plain block.
      const pinned = props.image_layout === 'single' || props.image_layout === 'carousel' ? props.image_layout : null
      const layout = pinned || (imgs.length > 1 ? 'scroll' : 'stacked')
      const showCap = props.show_caption === true
      let wraps = ''
      imgs.forEach((img, i) => {
        const capText = img.alt || `Page ${i + 1} / ${imgs.length}`
        const cap = showCap ? `<p class="sai-fsgfrd01__caption">${esc(capText)}</p>` : ''
        // width/height attrs let the browser reserve the exact aspect-ratio box
        // before the bytes load, so the shimmer occupies the final height and the
        // popup/drawer doesn't fluctuate when the image swaps in.
        const dim = img.w && img.h ? ` width="${img.w}" height="${img.h}"` : ''
        wraps += `<div class="sai-fsgfrd01__img-wrap" data-index="${i}" data-active="${i === 0 ? 'true' : 'false'}"><img src="${esc(img.url)}" alt="${esc(img.alt || '')}"${dim} loading="lazy" decoding="async">${cap}</div>`
      })
      let nav = ''
      if (layout === 'single' && imgs.length > 1) {
        let dots = ''
        imgs.forEach((_, i) => {
          dots += `<button type="button" class="sai-fsgfrd01__dot" data-action="goto" data-index="${i}" data-active="${i === 0 ? 'true' : 'false'}" aria-label="Page ${i + 1}"></button>`
        })
        nav = `<div class="sai-fsgfrd01__nav-bar"><button type="button" class="sai-fsgfrd01__nav" data-action="prev" aria-label="Previous image">${chevron('left')}</button><div class="sai-fsgfrd01__dots">${dots}</div><button type="button" class="sai-fsgfrd01__nav" data-action="next" aria-label="Next image">${chevron('right')}</button></div>`
      }
      return `<div class="sai-fsgfrd01__gallery" data-layout="${layout}">${wraps}</div>${nav}`
    }

    _galleryGoto(idx) {
      const root = this._rootEl || this
      const wraps = root.querySelectorAll('.sai-fsgfrd01__img-wrap')
      if (!wraps.length) return
      const clamped = Math.max(0, Math.min(wraps.length - 1, idx))
      this._galleryIndex = clamped
      for (let i = 0; i < wraps.length; i++) wraps[i].dataset.active = i === clamped ? 'true' : 'false'
      for (const dot of root.querySelectorAll('.sai-fsgfrd01__dot')) {
        dot.dataset.active = Number.parseInt(dot.dataset.index, 10) === clamped ? 'true' : 'false'
      }
      const carousel = root.querySelector('.sai-fsgfrd01__gallery[data-layout="carousel"]')
      if (carousel && wraps[clamped] && wraps[clamped].scrollIntoView) {
        wraps[clamped].scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' })
      }
      this._track(`${FEATURE_SLUG}:page_change_elem_clicked`, { to: clamped })
    }

    _foot() {
      const props = this._props()
      if (props.show_cta === false) return ''
      // Image-only (gallery) has nothing to add to cart → a "back to shopping"
      // button that just closes the sheet, not a disabled Buy Now.
      if (this._resolved.body === 'gallery') {
        const back = props.gallery_cta_text || 'Back to Shopping'
        if (props.gallery_cta_action === 'link' && props.gallery_cta_url) {
          return `<div class="sai-fsgfrd01__foot"><a class="sai-fsgfrd01__buy sai-fsgfrd01__buy--back" href="${esc(props.gallery_cta_url)}">${esc(back)}</a></div>`
        }
        return `<div class="sai-fsgfrd01__foot"><button type="button" class="sai-fsgfrd01__buy sai-fsgfrd01__buy--back" data-action="close">${esc(back)}</button></div>`
      }
      if (this._resolved.body !== 'table') return ''
      const label = props.cta_text || 'ADD TO CART'
      return `<div class="sai-fsgfrd01__foot"><button type="button" class="sai-fsgfrd01__buy" data-action="cta"${this._pickedSize ? '' : ' disabled'}>${esc(label)}</button></div>`
    }

    _bindEvents() {
      if (this._eventsBound) return
      this._eventsBound = true

      // One delegated handler. Bound to BOTH the host (the trigger lives here)
      // and the modal root (which is portaled out to <body> on open — see
      // _open — so its clicks no longer bubble through the host).
      this._onClick = (e) => {
        const t = e.target.closest('[data-action]')
        if (!t) return
        const action = t.dataset.action
        if (action === 'open') return this._open()
        if (action === 'close') return this._close()
        if (action === 'unit') return this._setUnit(t.dataset.unit)
        if (action === 'color') return this._pickColor(t.dataset.colorValue)
        if (action === 'extra') return this._pickExtra(t.dataset.extraKey, t.dataset.extraValue)
        if (action === 'cta') return this._cta()
        if (action === 'prev') return this._galleryGoto((this._galleryIndex || 0) - 1)
        if (action === 'next') return this._galleryGoto((this._galleryIndex || 0) + 1)
        if (action === 'goto') return this._galleryGoto(Number.parseInt(t.dataset.index, 10) || 0)
        if (action === 'guide-open') {
          const gs = (this._rootEl || this).querySelector('.sai-fsgfrd01__guide')
          if (gs) gs.dataset.open = 'true'
          this._track(`${FEATURE_SLUG}:guide_open_elem_clicked`, {})
          return
        }
        if (action === 'guide-close') {
          const gs = (this._rootEl || this).querySelector('.sai-fsgfrd01__guide')
          if (gs) {
            gs.dataset.open = 'false'
            const v = gs.querySelector('video')
            if (v) { try { v.pause() } catch (e) {} }
          }
          return
        }
        if (action === 'pick-size') {
          const row = e.target.closest('[data-size-label]')
          if (row) this._pickSize(row.dataset.sizeLabel)
        }
      }
      this.addEventListener('click', this._onClick)
      if (this._rootEl) this._rootEl.addEventListener('click', this._onClick)

      this._onKey = (e) => {
        if (e.key === 'Escape' && this._isOpen) this._close()
      }
      document.addEventListener('keydown', this._onKey)

      // External trigger: the size selectors (fszfrd01 size-guide row, fszfrd02
      // kids finder) open this drawer by dispatching a window event. Listening
      // here means any current/future trigger works without knowing our DOM.
      this._onExternalOpen = () => { if (this._resolved) this._open() }
      window.addEventListener('spectrum:size-guide-open', this._onExternalOpen)

      // Forward sync (page → chart): listen to `spectrum:variant-change` — the single
      // notification every Frido picker dispatches alongside its `?variant` write — so
      // the chart's selected-size highlight tracks a variant chosen anywhere on the PDP.
      // The URL stays the source of truth (gallery + cart read it); we read the id from
      // the event detail, falling back to `?variant` for any dispatcher that omits it.
      this._onVariantChange = (e) => {
        let vid = e && e.detail && e.detail.variantId != null ? String(e.detail.variantId) : null
        if (!vid) {
          try {
            vid = new URLSearchParams(window.location.search).get('variant')
          } catch (_) {}
        }
        if (!vid || String(vid) === String(this._extVariantId)) return
        this._extVariantId = vid
        const pool = this._pool
        const chart = pool && pool.size_chart
        if (!chart) return
        const nv = ((pool.product && pool.product.variants) || []).find(
          (v) => String(v.id) === String(vid),
        )
        if (!nv) return
        // Reconcile the chart's FULL selection (size + colour + every extra axis)
        // to the variant chosen elsewhere on the PDP — not size alone. Colour lives
        // in fswfrd01, which dispatches this same event; tracking size only would let
        // _pickedColor drift, and a later in-chart size pick would then resolve (and
        // add to cart) the wrong-colour variant. Availability/OOS math also depends
        // on colour, so re-render whenever any axis actually moved.
        let changed = false
        let colourChanged = false
        const extraChanged = []
        const pos = getBasisOptionPosition(pool, chart)
        const sv = pos ? nv['option' + pos] : null
        if (sv && sv !== this._pickedSize && (chart.sizes || []).some((s) => s.label === sv)) {
          this._pickedSize = sv
          changed = true
        }
        if (pool.color_option && pool.color_option.position) {
          const cv = nv['option' + pool.color_option.position]
          if (cv && cv !== this._pickedColor) {
            this._pickedColor = cv
            colourChanged = true
            changed = true
          }
        }
        for (const ax of getExtraAxes(pool, chart)) {
          const ev = nv[ax.key]
          if (ev && ax.values.includes(ev) && ev !== this._pickedExtra[ax.key]) {
            this._pickedExtra[ax.key] = ev
            extraChanged.push(ax.key)
            changed = true
          }
        }
        if (changed) {
          // Repaint the controls whose value moved (colour swatch / extra pills), plus
          // the table (its OOS strikes depend on colour/extra) and the CTA.
          if (colourChanged) this._syncColorUI()
          for (const k of extraChanged) this._syncExtraUI(k)
          this._rerenderTable()
          this._updateCta()
        }
      }
      window.addEventListener('spectrum:variant-change', this._onVariantChange)
    }

    disconnectedCallback() {
      if (this._onKey) document.removeEventListener('keydown', this._onKey)
      if (this._onExternalOpen) window.removeEventListener('spectrum:size-guide-open', this._onExternalOpen)
      if (this._onVariantChange) window.removeEventListener('spectrum:variant-change', this._onVariantChange)
      // The portaled overlay is a <body> child now — remove it so it doesn't
      // outlive the host element.
      if (this._rootEl && this._rootEl.parentElement === document.body) this._rootEl.remove()
      // Restore the body if we're torn down mid-open — the position:fixed lock
      // lives on <body>, not on our removed element, so it would otherwise persist.
      if (this._isOpen) this._unlockBody()
    }

    _root() {
      return this._rootEl || this.querySelector('.sai-fsgfrd01__root')
    }
    _open() {
      const root = this._root()
      if (!root) return
      // Portal the overlay to <body> so position:fixed escapes any theme CSS
      // containment (transform / filter / container-type — Horizon and other
      // container-query themes trap fixed descendants inside the section box).
      if (root.parentElement !== document.body) document.body.appendChild(root)
      // Clear any leftover exit state if reopened mid-close.
      root.classList.remove('sai-fsgfrd01__root--closing')
      root.__closing = false
      root.dataset.open = 'true'
      this._isOpen = true
      // Scroll-lock via position:fixed body. body{overflow:hidden} alone does NOT
      // hold the background on desktop (the scroll container is <html>, not <body>),
      // so the page scrolled behind the overlay. Fixing the body (preserving the
      // scroll offset in `top`) locks every device; restore + scrollTo on close.
      this._scrollY = window.scrollY || window.pageYOffset || 0
      this._prevBody = {
        overflow: document.body.style.overflow,
        position: document.body.style.position,
        top: document.body.style.top,
        width: document.body.style.width,
      }
      document.body.style.overflow = 'hidden'
      document.body.style.position = 'fixed'
      document.body.style.top = `-${this._scrollY}px`
      document.body.style.width = '100%'
      // Start the link-row thumbnail video (autoplay is unreliable; _open runs
      // inside the trigger click so play() is gesture-allowed). If play stays
      // blocked, seek a little so the visible frame isn't the blank frame 0.
      const tv = root.querySelector('.sai-fsgfrd01__linkrow-thumb video')
      if (tv) {
        const seek = () => { try { tv.currentTime = Math.min(0.4, (tv.duration || 4) * 0.1) } catch (e) {} }
        if (tv.readyState >= 1) seek()
        else tv.addEventListener('loadeddata', seek, { once: true })
        try { const p = tv.play(); if (p && p.catch) p.catch(() => {}) } catch (e) {}
      }
      this._wireMediaShimmer(root)
      this._track(`${FEATURE_SLUG}:open_elem_clicked`, { product_url: this._pool.product ? this._pool.product.url : null })
    }

    // Show an animated skeleton on each media card until its image finishes
    // loading, so opening the popup doesn't flash blank/pop-in. No-op for images
    // already decoded (fast connections) — the class is added only when pending.
    _wireMediaShimmer(root) {
      const wraps = root.querySelectorAll('.sai-fsgfrd01__media, .sai-fsgfrd01__img-wrap')
      wraps.forEach((wrap) => {
        const imgs = [...wrap.querySelectorAll('img')]
        if (!imgs.length) return
        const loaded = (im) => im.complete && im.naturalWidth > 0
        if (imgs.every(loaded)) return
        wrap.classList.add('is-loading')
        const settle = () => { if (imgs.every((im) => loaded(im) || im.dataset.saiErr === '1')) wrap.classList.remove('is-loading') }
        imgs.forEach((im) => {
          im.addEventListener('load', settle, { once: true })
          im.addEventListener('error', () => { im.dataset.saiErr = '1'; settle() }, { once: true })
        })
      })
    }
    // Undo the position:fixed scroll-lock installed in _open() and restore the
    // scroll position. The restore MUST be instant: the theme sets a global
    // scroll-behavior:smooth, so a plain window.scrollTo animates it — the visible
    // glitch of the page scrolling from the top back down to the size-chart
    // section. Force scroll-behavior:auto around it. Called from _close() AND
    // disconnectedCallback (host removed while open), so unmounting mid-open can't
    // leave <body> pinned in position:fixed.
    _unlockBody() {
      const b = this._prevBody || {}
      document.body.style.overflow = b.overflow ?? ''
      document.body.style.position = b.position ?? ''
      document.body.style.top = b.top ?? ''
      document.body.style.width = b.width ?? ''
      if (this._scrollY != null) {
        const de = document.documentElement
        const prevSB = de.style.scrollBehavior
        de.style.scrollBehavior = 'auto'
        window.scrollTo(0, this._scrollY)
        de.style.scrollBehavior = prevSB
      }
    }
    _close() {
      const root = this._root()
      if (!root) return
      if (root.__closing) return
      this._isOpen = false
      this._unlockBody()
      this._track(`${FEATURE_SLUG}:close_elem_clicked`, {})

      // Play the exit animation before hiding. The --closing class keeps the
      // overlay displayed (data-open is set false so the open animation stops
      // matching) while the panel/backdrop animate out, then it's hidden.
      const hide = () => {
        root.dataset.open = 'false'
        root.classList.remove('sai-fsgfrd01__root--closing')
        root.__closing = false
      }
      const reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches
      if (reduce) { hide(); return }
      root.__closing = true
      root.classList.add('sai-fsgfrd01__root--closing')
      root.dataset.open = 'false'
      let done = false
      const finish = () => {
        if (done) return
        done = true
        root.removeEventListener('animationend', onEnd)
        hide()
      }
      const onEnd = (e) => {
        if (e.target && e.target.classList && e.target.classList.contains('sai-fsgfrd01__panel')) finish()
      }
      root.addEventListener('animationend', onEnd)
      setTimeout(finish, 400)
    }

    _setUnit(unit) {
      if (!unit || unit === this._unit) return
      this._unit = unit
      this._rerenderTable()
      this._track(`${FEATURE_SLUG}:unit_toggle_elem_clicked`, { unit })
    }

    // Write the current full selection out to the PDP — URL `?variant`, the scoped
    // ATC form input, and the `spectrum:variant-change` event — when auto-select is on
    // and the selection resolves a BUYABLE variant. Shared by every in-chart axis
    // (size/colour/extra) so picking any of them flows out the same way the forward
    // listener flows page changes in. pickVariantForAtc falls back to an OOS variants[0];
    // the availability guard keeps an unbuyable id out of the URL/form. Echo-guarded so
    // the event we dispatch isn't re-consumed by our own forward listener.
    _syncVariantOut() {
      if (this._props().auto_select_variant !== true) return
      const variant = pickVariantForAtc(this._pool, this._pool.size_chart, this._pickedSize, this._pickedColor, this._pickedExtra)
      if (!variant || !variant.available) return
      this._extVariantId = String(variant.id)
      try {
        const url = new URL(window.location.href)
        url.searchParams.set('variant', String(variant.id))
        window.history.replaceState({}, '', url.toString())
      } catch (e) {}
      // Scope the ATC-form write to forms whose current value is one of THIS product's
      // variants, so upsell/other-product forms on the page are never touched.
      try {
        const vids = {}
        ;((this._pool.product && this._pool.product.variants) || []).forEach((vv) => {
          vids[String(vv.id)] = 1
        })
        document.querySelectorAll('form[action*="/cart/add"] input[name="id"]').forEach((input) => {
          if (vids[String(input.value)]) {
            input.value = String(variant.id)
            input.dispatchEvent(new Event('change', { bubbles: true }))
          }
        })
      } catch (e) {}
      window.dispatchEvent(new CustomEvent('spectrum:variant-change', { detail: { variantId: variant.id } }))
    }

    // Repaint the colour-row selection UI from `this._pickedColor` (label + swatch
    // is-sel/aria-pressed). Shared by _pickColor and the forward listener so a colour
    // chosen anywhere on the PDP highlights correctly in the drawer.
    _syncColorUI() {
      const root = this._rootEl || this
      const label = root.querySelector('[data-color-label]')
      if (label && this._pickedColor != null) label.textContent = this._pickedColor
      for (const btn of root.querySelectorAll('[data-action="color"]')) {
        const sel = btn.dataset.colorValue === this._pickedColor
        btn.classList.toggle('is-sel', sel)
        btn.setAttribute('aria-pressed', sel ? 'true' : 'false')
      }
    }

    // Repaint one extra-axis (e.g. Hand) selection UI from `this._pickedExtra[key]`.
    _syncExtraUI(key) {
      const root = this._rootEl || this
      const label = root.querySelector(`[data-extra-label="${key}"]`)
      if (label && this._pickedExtra[key] != null) label.textContent = this._pickedExtra[key]
      for (const btn of root.querySelectorAll(`[data-action="extra"][data-extra-key="${key}"]`)) {
        const sel = btn.dataset.extraValue === this._pickedExtra[key]
        btn.classList.toggle('is-sel', sel)
        btn.setAttribute('aria-pressed', sel ? 'true' : 'false')
      }
    }

    _pickSize(label) {
      this._pickedSize = label
      this._rerenderTable()
      this._updateCta()
      // Any in-chart axis change (size here; colour/extra below) flows out to the PDP.
      this._syncVariantOut()
      const variant = pickVariantForAtc(this._pool, this._pool.size_chart, label, this._pickedColor, this._pickedExtra)
      this._track(`${FEATURE_SLUG}:select_size`, {
        size: label,
        variant_id: variant ? variant.id : null,
        available: isSizeAvailable(this._pool, this._pool.size_chart, label, this._pickedColor, this._pickedExtra),
      })
    }

    _pickColor(value) {
      if (value === this._pickedColor) return
      this._pickedColor = value
      this._syncColorUI()
      this._rerenderTable()
      this._updateCta()
      this._syncVariantOut()
      this._track(`${FEATURE_SLUG}:select_color`, { color: value })
    }

    _pickExtra(key, value) {
      if (!key || this._pickedExtra[key] === value) return
      this._pickedExtra[key] = value
      this._syncExtraUI(key)
      // Availability (OOS strike) can differ per axis value, so re-render the table.
      this._rerenderTable()
      this._updateCta()
      this._syncVariantOut()
      this._track(`${FEATURE_SLUG}:select_option`, { name: key, value })
    }

    _rerenderTable() {
      const root = this._rootEl || this
      const card = root.querySelector('.sai-fsgfrd01__card')
      const unit = root.querySelector('.sai-fsgfrd01__unit')
      if (!card) return
      const html = this._tableCard()
      // _tableCard() returns [unit toggle?] + card; swap both in place.
      const wrap = document.createElement('div')
      wrap.innerHTML = html
      const newCard = wrap.querySelector('.sai-fsgfrd01__card')
      const newUnit = wrap.querySelector('.sai-fsgfrd01__unit')
      if (newCard) card.replaceWith(newCard)
      if (unit && newUnit) unit.replaceWith(newUnit)
    }

    _updateCta() {
      const cta = (this._rootEl || this).querySelector('[data-action="cta"]')
      if (!cta) return
      const props = this._props()
      const variant = pickVariantForAtc(this._pool, this._pool.size_chart, this._pickedSize, this._pickedColor, this._pickedExtra)
      const oos = !variant || !variant.available
      cta.disabled = !this._pickedSize || oos
      cta.classList.toggle('is-oos', !!this._pickedSize && oos)
      cta.textContent = this._pickedSize && oos ? props.cta_oos_text || 'Out of Stock' : props.cta_text || 'ADD TO CART'
    }

    _cta() {
      const props = this._props()
      const action = props.cta_action || 'add_to_cart'
      if (action === 'close') return this._close()

      const variant = pickVariantForAtc(this._pool, this._pool.size_chart, this._pickedSize, this._pickedColor, this._pickedExtra)
      if (!variant || !variant.available) return

      const payload = { size: this._pickedSize, color: this._pickedColor, variant_id: variant.id, quantity: 1 }
      this._track(`${FEATURE_SLUG}:add_to_cart`, payload)
      this._emit(`${FEATURE_SLUG}:add_to_cart`, payload)

      const cart = window.Spectrum && window.Spectrum.cart
      if (!cart || typeof cart.add !== 'function') {
        this._track(`${FEATURE_SLUG}:add_to_cart_error`, { variant_id: variant.id, error_message: 'Spectrum cart API unavailable' })
        return
      }
      const btn = (this._rootEl || this).querySelector('[data-action="cta"]')
      if (btn) btn.classList.add('is-adding')
      const run =
        (action === 'checkout' || action === 'add_to_cart') && typeof cart.addAndOpen === 'function'
          ? cart.addAndOpen({ id: variant.id, quantity: 1 }, { sourceId: `spectrum-${SNIPPET_ID}` })
          : cart.add({ id: variant.id, quantity: 1 }, { sourceId: `spectrum-${SNIPPET_ID}` })
      Promise.resolve(run)
        .then((res) => {
          if (res && res.ok === false) throw new Error((res.error && res.error.message) || 'Add to cart failed')
          this._track(`${FEATURE_SLUG}:added_to_cart`, payload)
          this._emit(`${FEATURE_SLUG}:added_to_cart`, payload)
          if (action === 'checkout') window.location.href = '/checkout'
          else this._close()
        })
        .catch((err) => {
          this._track(`${FEATURE_SLUG}:add_to_cart_error`, { variant_id: variant.id, error_message: err && err.message ? err.message : String(err) })
        })
        .finally(() => { if (btn) btn.classList.remove('is-adding') })
    }
  }

  if (!customElements.get(TAG)) customElements.define(TAG, SizeGuide)

  function bindAllContainers() {
    const snippetApi = window.__spectrumAi && window.__spectrumAi.snippet
    const containers = document.querySelectorAll(`[data-spectrum-instance-id][data-spectrum-snippet-id="${SNIPPET_ID}"]`)
    let bound = 0
    for (const node of containers) {
      const el = node.querySelector(TAG)
      if (!el) continue
      // First paint straight from the server-rendered pool — never waits on
      // the SDK (which may load after DOMContentLoaded).
      if (!el._painted) {
        const initPool = readSnippetPool(node)
        if (initPool && (initPool.size_chart || (initPool.images && initPool.images.length) || initPool.guide)) {
          el._painted = true
          el.applyVariant({}, initPool)
        }
      }
      if (snippetApi && typeof snippetApi.bind === 'function' && !el._wrapBound) {
        el._wrapBound = true
        const handles = snippetApi.bind(node, (ctx) => {
          const e = node.querySelector(TAG)
          if (!e) return
          e.applyVariant(ctx.variant ? ctx.variant.content : {}, readSnippetPool(node))
        })
        if (handles) el.setAnalytics(handles.track, handles.emit)
      }
      bound++
    }
    if (bound > 0) return
    // Standalone / dev — self-init from the sibling pool.
    for (const el of document.querySelectorAll(TAG)) {
      if (el._poolApplied) continue
      const parent = el.closest('[data-spectrum-lq-snippet]') || el.parentElement
      if (!parent) continue
      const pool = readSnippetPool(parent)
      if (pool && (pool.size_chart || (pool.images && pool.images.length))) {
        el._poolApplied = true
        el.applyVariant({}, pool)
      }
    }
  }

  if (typeof globalThis !== 'undefined' && globalThis.__SAI_TEST_HARNESS__ === true) {
    globalThis.__saiDsrspa2d = {
      getColumns,
      getUnitOptions,
      getMeasurementValue,
      getBasisOptionPosition,
      findVariantsForSelection,
      isSizeAvailable,
      pickVariantForAtc,
      resolveSizeGuide,
      readSnippetPool,
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scheduleBind, { once: true })
  } else {
    scheduleBind()
  }
  function scheduleBind() {
    bindAllContainers()
    let tries = 0
    const t = setInterval(() => {
      bindAllContainers()
      const api = window.__spectrumAi && window.__spectrumAi.snippet
      if ((api && typeof api.bind === 'function') || ++tries > 20) clearInterval(t)
    }, 500)
  }

  // The storefront SDK can re-mount slot wrappers after load (conflict
  // resolution / vis flips) — a replaced wrapper holds a fresh unpainted
  // element, so rebind on DOM changes, debounced.
  const __saiRelevant = (records) => {
    for (const r of records) {
      for (const n of r.addedNodes) {
        if (n.nodeType !== 1) continue
        if ((n.matches && n.matches(`${TAG}, [data-spectrum-snippet-id="${SNIPPET_ID}"], [data-spectrum-lq-snippet]`)) ||
            (n.querySelector && n.querySelector(`${TAG}, [data-spectrum-snippet-id="${SNIPPET_ID}"]`))) return true
      }
    }
    return false
  }
  const __saiMo = new MutationObserver((records) => {
    if (__saiMo._t || !__saiRelevant(records)) return
    __saiMo._t = setTimeout(() => { __saiMo._t = null; bindAllContainers() }, 150)
  })
  __saiMo.observe(document.documentElement, { childList: true, subtree: true })
  setTimeout(function () { try { __saiMo.disconnect() } catch (e) {} }, 30000)
})()
